import React, { useRef, useState, Suspense, useEffect, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment, Html, Center } from '@react-three/drei';
import * as THREE from 'three';
import { Molecule, RenderMode, ELEMENTS } from '../../types';
import { useVisualizationStore } from '../../store';

interface MoleculeViewerProps {
  molecule: Molecule | null;
  settings?: Partial<import('../../types').VisualizationSettings>;
  onAtomClick?: (atomId: string) => void;
  onBondClick?: (bondId: string) => void;
  onBackgroundClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

function getCentroid(atoms: { x: number; y: number; z: number }[]): [number, number, number] {
  if (atoms.length === 0) return [0, 0, 0];
  
  let x = 0, y = 0, z = 0;
  for (const atom of atoms) {
    x += atom.x;
    y += atom.y;
    z += atom.z;
  }
  const n = atoms.length;
  return [x / n, y / n, z / n];
}

function AtomSphere({ 
  atom, 
  scale = 0.4, 
  color,
  showLabel = false,
  onClick 
}: { 
  atom: { id: string; element: string; x: number; y: number; z: number };
  scale?: number;
  color?: string;
  showLabel?: boolean;
  onClick?: () => void;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  
  const element = ELEMENTS[atom.element] || ELEMENTS['C'];
  const radius = element.covalentRadius * scale * 0.5;
  const atomColor = color || element.color;
  
  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.scale.setScalar(hovered ? 1.15 : 1);
    }
  });

  return (
    <group position={[atom.x, atom.y, atom.z]}>
      <mesh
        ref={meshRef}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = 'auto';
        }}
      >
        <sphereGeometry args={[radius, 32, 32]} />
        <meshStandardMaterial
          color={atomColor}
          roughness={0.3}
          metalness={0.2}
          emissive={atomColor}
          emissiveIntensity={hovered ? 0.4 : 0.15}
        />
      </mesh>
      {showLabel && (
        <Html distanceFactor={12}>
          <div style={{
            background: 'rgba(0,0,0,0.85)',
            padding: '3px 8px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 'bold',
            color: 'white',
            whiteSpace: 'nowrap',
            border: '1px solid rgba(255,255,255,0.2)',
          }}>
            {atom.element}
          </div>
        </Html>
      )}
    </group>
  );
}

function BondCylinder({
  bond,
  start,
  end,
  thickness = 0.1,
  color = '#606060',
  onClick
}: {
  bond?: { id: string; type: number };
  start: { x: number; y: number; z: number };
  end: { x: number; y: number; z: number };
  thickness?: number;
  color?: string;
  onClick?: () => void;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  
  const startVec = new THREE.Vector3(start.x, start.y, start.z);
  const endVec = new THREE.Vector3(end.x, end.y, end.z);
  const direction = new THREE.Vector3().subVectors(endVec, startVec);
  const length = direction.length();
  
  // Skip bonds that are too long or invalid
  if (length < 0.1 || length > 5) return null;
  
  const midpoint = new THREE.Vector3().addVectors(startVec, endVec).multiplyScalar(0.5);
  
  const quaternion = new THREE.Quaternion();
  if (direction.length() > 0.001) {
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  }
  
  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.scale.setScalar(hovered ? 1.15 : 1);
    }
  });

  const adjustedThickness = thickness * (bond?.type === 2 ? 1.3 : bond?.type === 3 ? 1.5 : 1);

  return (
    <group position={midpoint} quaternion={quaternion}>
      <mesh
        ref={meshRef}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = 'auto';
        }}
      >
        <cylinderGeometry args={[adjustedThickness, adjustedThickness, length, 16]} />
        <meshStandardMaterial
          color={hovered ? '#808080' : color}
          roughness={0.35}
          metalness={0.3}
          emissive={hovered ? '#404040' : '#1a1a1a'}
          emissiveIntensity={0.3}
        />
      </mesh>
    </group>
  );
}

function MoleculeGroup({ 
  molecule,
  renderMode,
  showHydrogens,
  showLabels,
  onAtomClick,
  onBondClick
}: {
  molecule: Molecule;
  renderMode: RenderMode;
  showHydrogens: boolean;
  showLabels: boolean;
  onAtomClick?: (id: string) => void;
  onBondClick?: (id: string) => void;
}) {
  // Filter atoms based on showHydrogens setting
  const filteredAtoms = showHydrogens 
    ? molecule.atoms 
    : molecule.atoms.filter(a => a.element !== 'H');
  
  // Create a map from original index to filtered atom
  const originalToFiltered = new Map<number, number>();
  filteredAtoms.forEach((atom, filteredIndex) => {
    originalToFiltered.set(filteredIndex, filteredIndex);
  });
  
  // Calculate centroid and adjust atoms
  const positions = filteredAtoms.map(a => ({ x: a.x, y: a.y, z: a.z }));
  const centroid = getCentroid(positions);

  const adjustedAtoms = filteredAtoms.map((atom, index) => ({
    ...atom,
    x: atom.x - centroid[0],
    y: atom.y - centroid[1],
    z: atom.z - centroid[2],
    originalIndex: index,
  }));

  // Create a map from adjusted atom index to the adjusted atom
  const adjustedAtomMap = new Map<number, typeof adjustedAtoms[0]>();
  adjustedAtoms.forEach((atom) => {
    adjustedAtomMap.set(atom.originalIndex, atom);
  });

  // Scale factor based on render mode
  const getScale = (element: string) => {
    const baseScale = renderMode === RenderMode.STICK ? 0.25 : 
                      renderMode === RenderMode.SPACE_FILLING ? 0.9 : 0.45;
    return baseScale;
  };

  return (
    <Center>
      {/* Render atoms */}
      {adjustedAtoms.map((atom) => {
        const scale = getScale(atom.element);
        return (
          <AtomSphere
            key={atom.id}
            atom={atom}
            scale={scale}
            showLabel={showLabels}
            onClick={() => onAtomClick?.(atom.id)}
          />
        );
      })}

      {/* Render bonds - only for bonds between shown atoms */}
      {molecule.bonds
        .filter(bond => {
          // Check if both atoms are in the filtered set
          const atom1InFiltered = bond.atomIndex1 < filteredAtoms.length;
          const atom2InFiltered = bond.atomIndex2 < filteredAtoms.length;
          return atom1InFiltered && atom2InFiltered;
        })
        .map((bond) => {
          const atom1 = adjustedAtomMap.get(bond.atomIndex1);
          const atom2 = adjustedAtomMap.get(bond.atomIndex2);
          
          if (!atom1 || !atom2) return null;
          
          const bondColor = bond.type === 2 ? '#505050' : bond.type === 3 ? '#404040' : '#606060';
          const thickness = renderMode === RenderMode.STICK ? 0.15 : 
                           renderMode === RenderMode.SPACE_FILLING ? 0.05 : 0.1;

          return (
            <BondCylinder
              key={bond.id}
              bond={bond}
              start={atom1}
              end={atom2}
              thickness={thickness}
              color={bondColor}
              onClick={() => onBondClick?.(bond.id)}
            />
          );
        })}
    </Center>
  );
}

function EmptyViewer() {
  return (
    <Html center>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
        padding: '2.5rem',
        background: 'rgba(30, 41, 59, 0.95)',
        borderRadius: '1.25rem',
        backdropFilter: 'blur(12px)',
        border: '1px solid var(--color-border)',
        maxWidth: '320px',
      }}>
        <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--color-primary-400)' }}>
          <circle cx="12" cy="12" r="10" />
          <circle cx="8" cy="10" r="2" strokeWidth="1.5" />
          <circle cx="15" cy="8" r="2" strokeWidth="1.5" />
          <circle cx="14" cy="16" r="2" strokeWidth="1.5" />
          <line x1="8" y1="10" x2="14" y2="16" strokeWidth="1.5" />
          <line x1="15" y1="8" x2="8" y2="10" strokeWidth="1.5" />
          <line x1="14" y1="16" x2="15" y2="8" strokeWidth="1.5" />
        </svg>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.95rem', textAlign: 'center' }}>
          No molecule loaded
        </p>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', textAlign: 'center', opacity: 0.8 }}>
          Enter a SMILES string or select an example molecule to visualize
        </p>
      </div>
    </Html>
  );
}

export function MoleculeViewer({ 
  molecule, 
  onAtomClick, 
  onBondClick,
  className = '',
  style 
}: MoleculeViewerProps) {
  const { settings } = useVisualizationStore();
  const renderMode = settings?.renderMode || RenderMode.BALL_AND_STICK;
  const showHydrogens = settings?.showHydrogens ?? true;
  const showLabels = settings?.showAtomLabels ?? false;
  const containerRef = useRef<HTMLDivElement>(null);

  // Prevent pull-to-refresh and other unwanted touch behaviors
  useEffect(() => {
    const preventDefault = (e: TouchEvent) => {
      // Prevent pull-to-refresh on the viewer
      if (e.target === containerRef.current || containerRef.current?.contains(e.target as Node)) {
        // Allow scrolling only for multi-touch gestures
        if (e.touches.length === 1) {
          e.preventDefault();
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchmove', preventDefault, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('touchmove', preventDefault);
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`viewer-container ${className}`} 
      style={style}
      onTouchStart={(e) => {
        // Track touch start for gestures
        if (e.touches.length === 2) {
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          (e.target as HTMLElement).dataset.touchStartDistance = distance.toString();
        }
      }}
    >
      <Canvas
        shadows={settings?.enableShadows ?? false}
        gl={{
          antialias: settings?.antialiasing ?? true,
          alpha: true,
          preserveDrawingBuffer: true,
        }}
        camera={{ position: [15, 15, 15], fov: 45 }}
        onCreated={({ gl, camera }) => {
          // Configure renderer for better mobile performance
          gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        }}
      >
        <OrbitControls
          enableDamping
          dampingFactor={0.08}
          minDistance={3}
          maxDistance={60}
          enablePan={true}
          autoRotate={false}
          // Touch-friendly controls
          enableRotate={true}
          rotateSpeed={0.5}
          zoomSpeed={0.8}
          panSpeed={0.5}
          touches={{
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_ROTATE
          }}
        />
        
        <ambientLight intensity={0.5} />
        <directionalLight 
          position={[10, 15, 10]} 
          intensity={1.0} 
          castShadow 
        />
        <directionalLight 
          position={[-10, -10, -5]} 
          intensity={0.4} 
        />
        <pointLight position={[0, 10, 0]} intensity={0.5} />
        
        <Environment preset="city" background={false} />
        
        <Suspense fallback={null}>
          {molecule ? (
            <MoleculeGroup
              molecule={molecule}
              renderMode={renderMode}
              showHydrogens={showHydrogens}
              showLabels={showLabels}
              onAtomClick={onAtomClick}
              onBondClick={onBondClick}
            />
          ) : (
            <EmptyViewer />
          )}
        </Suspense>
      </Canvas>
    </div>
  );
}

